import React, { useMemo, useState } from 'react';
import isEqual from 'lodash/isEqual';
import { AiFillStar, AiOutlineMinus, AiOutlinePlus } from 'react-icons/ai';
import { MdClose, MdOutlinePercent } from 'react-icons/md';
import RangeSlider from '@PROJECT_ROOT/assets/responsive-frontend/js/parts/rangeSlider';
import CheckBoxField from '@PROJECT_ROOT/assets/responsive-frontend/js/formFields/checkBoxField';
import RadioBoxField from '@PROJECT_ROOT/assets/responsive-frontend/js/formFields/radioBoxField';

export default function Filters({
    selectedFilters,
    selectedFiltersCount,
    data,
    sortData,
    handleSort,
    isLoading,
    handleFilterChange,
    handleCheckboxChange,
    handleMinChange,
    handleMaxChange,
    handleBathrooms,
    handleCenterDistance,
    handleBeachDistance,
    defaultCheckedCenter,
    defaultCheckedBeach,
    handleAccommodationScore,
    filtersDefaults,
    handleResetFilters,
    initiallySelectedFilters,
    handleApplyFilters,
    searchResultsCount,
    isForModal,
}) {
    const numberOfFilterItemsToShowWhenCollapsed = 4;
    const indexOfLastFilterItemToShowWhenCollapsed = numberOfFilterItemsToShowWhenCollapsed - 1;
    const [expandedFilters, setExpandedFilters] = useState({
        filterChips: false,
        additionalServices: false,
        accommodationCategory: false,
    });
    const getSelectedFiltersForChips = useMemo(() => {
        if (!data) return [];
        const { filters, extraFilters } = data;
        const selectedFiltersForChips = [];
        let priceRangeAdded = false;
        Object.keys(filtersDefaults)
            .forEach((key) => {
                if (selectedFilters[key] === filtersDefaults[key]) return;
                switch (key) {
                    case 'prc_min':
                    case 'prc_max':
                        if (priceRangeAdded) return;
                        selectedFiltersForChips.push({ key, text: `${filters.priceRange.title}: ${selectedFilters.prc_min} - ${selectedFilters.prc_max}€` });
                        priceRangeAdded = true;
                        break;
                    case 't': // Vrsta smještaja - string
                        selectedFilters[key].split('|').forEach((accommodationTypeValue) => {
                            const formElementData = filters.accommodationType.elements.find((accommodationType) => accommodationType.value === accommodationTypeValue);
                            if (!formElementData) return;
                            selectedFiltersForChips.push({ key, value: accommodationTypeValue, text: formElementData.title });
                        });
                        break;
                    case 'dc_t': // Prikaži objekte samo na popustu - string
                        selectedFiltersForChips.push({ key, value: filters.priceRange.onlyOnSale.value, text: filters.priceRange.onlyOnSale.title });
                        break;
                    case 'bedrooms': // Broj soba - int
                    case 'bathrooms': // Broj zahoda - int
                        selectedFiltersForChips.push({ key, text: `${filters.parts.numberBoxElements.find((el) => el.name === key).title}: ${selectedFilters[key]}` });
                        break;
                    case 'kit_room': // Kuhinja - boolean
                    case 'terrace': // Balkon - boolean
                    case 'outer_area': // Dvorište - boolean
                        selectedFiltersForChips.push({ key, text: filters.parts.checkBoxElements.find((el) => el.name === key).title });
                        break;
                    case 'pool': // bazen - boolean
                    case 'ac': // Klima uređaj - boolean
                    case 'outer_grill': // Vanjski roštilj - boolean
                    case 'private_pool': // Privatni bazen - boolean
                    case 'jacuzzi': // Jacuzzi - boolean
                    case 'tv': // Televizijia - boolean
                    case 'outer_view_sea': // Pogled na more - boolean
                    case 'pet': // Dozvoljeni ljubimci - int
                    case 'wm': // Perilica rublja - boolean
                    case 'moor': // Vez za čamac - boolean
                    case 'parking': // Parking - boolean
                    case 'food': // Doručak - boolean
                    case 'inet': // Internet - boolean
                    case 'fc': // Besplatan otkaz - boolean
                        selectedFiltersForChips.push({ key, text: filters.additionalServices.elements.find((el) => el.name === key).title });
                        break;
                    case 'category': // Kategorija smještaja - string
                        selectedFilters[key].split('|').forEach((accommodationCategoryValue) => {
                            const formElementData = filters.accommodationCategory.elements.find((accommodationCategory) => accommodationCategory.value === accommodationCategoryValue);
                            if (!formElementData) return;
                            selectedFiltersForChips.push({ key, value: accommodationCategoryValue, text: formElementData.title });
                        });
                        break;
                    case 'pos': // Etaža - string
                        selectedFilters[key].split('|').forEach((unitPositionValue) => {
                            const formElementData = filters.unitPosition.elements.find((unitPosition) => unitPosition.value === unitPositionValue);
                            if (!formElementData) return;
                            selectedFiltersForChips.push({ key, value: unitPositionValue, text: formElementData.title });
                        });
                        break;
                    case 'be_d': // Udaljenost od plaže - int
                        if (!filters.distances.beach.elements.map((element) => Number(element.value)).includes(selectedFilters[key])) return;
                        selectedFiltersForChips.push({ key, text: `${filters.distances.beach.title}: < ${selectedFilters[key]}m` });
                        break;
                    case 'ce_d': // Udaljenost od centra - int
                        if (!filters.distances.placeCenter.elements.map((element) => Number(element.value)).includes(selectedFilters[key])) return;
                        selectedFiltersForChips.push({ key, text: `${filters.distances.placeCenter.title}: < ${selectedFilters[key]}m` });
                        break;
                    case 'be_t': // Vrsta plaže - string
                        selectedFilters[key].split('|').forEach((beachTypeValue) => {
                            const formElementData = filters.beachType.elements.find((beachType) => beachType.value === beachTypeValue);
                            if (!formElementData) return;
                            selectedFiltersForChips.push({ key, value: beachTypeValue, text: formElementData.title });
                        });
                        break;
                    case 'land': // Dostupnost lokacije - string
                        selectedFilters[key].split('|').forEach((locationAvailabilityValue) => {
                            const formElementData = filters.locationAvailability.elements.find((locationAvailability) => locationAvailability.value === locationAvailabilityValue);
                            if (!formElementData) return;
                            selectedFiltersForChips.push({ key, value: locationAvailabilityValue, text: formElementData.title });
                        });
                        break;
                    case 'f_score': // Ocjena - string
                        if (!filters.accommodationScoreNew.elements.map((element) => element.value).includes(selectedFilters[key])) return;
                        selectedFiltersForChips.push({ key, text: `${filters.accommodationScoreNew.title}: ${selectedFilters[key]}` });
                        break;
                    case 'b_b': // Best buy - int
                        selectedFiltersForChips.push({ key, text: extraFilters.elements.find((el) => el.name === key).title });
                        break;
                    default:
                        break;
                }
            });
        return selectedFiltersForChips;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filtersDefaults, data, selectedFilters, selectedFiltersCount]);

    const handleFilterChipDelete = (key, value) => {
        switch (key) {
            case 'prc_min':
            case 'prc_max':
                handleMinChange(filtersDefaults.prc_min);
                handleMaxChange(filtersDefaults.prc_max);
                break;
            case 't': // Vrsta smještaja - string
                handleCheckboxChange(key, value);
                break;
            case 'dc_t': // Prikaži objekte samo na popustu - string
                handleFilterChange(key, 'all_discounts');
                break;
            case 'bedrooms': // Broj soba - int
            case 'bathrooms': // Broj zahoda - int
                handleBathrooms(key, 'reset');
                break;
            case 'kit_room': // Kuhinja - boolean
            case 'terrace': // Balkon - boolean
            case 'outer_area': // Dvorište - boolean
                handleFilterChange(key, value);
                break;
            case 'pool': // bazen - boolean
            case 'ac': // Klima uređaj - boolean
            case 'outer_grill': // Vanjski roštilj - boolean
            case 'private_pool': // Privatni bazen - boolean
            case 'jacuzzi': // Jacuzzi - boolean
            case 'tv': // Televizijia - boolean
            case 'outer_view_sea': // Pogled na more - boolean
            case 'pet': // Dozvoljeni ljubimci - int
            case 'wm': // Perilica rublja - boolean
            case 'moor': // Vez za čamac - boolean
            case 'parking': // Parking - boolean
            case 'food': // Doručak - boolean
            case 'inet': // Internet - boolean
            case 'fc': // Besplatan otkaz - boolean
                handleFilterChange(key, '');
                break;
            case 'category': // Kategorija smještaja - string
                handleCheckboxChange(key, value);
                break;
            case 'pos': // Etaža - string
                handleCheckboxChange(key, value.toString());
                break;
            case 'be_d': // Udaljenost od plaže - int
                handleBeachDistance(filtersDefaults[key]);
                break;
            case 'ce_d': // Udaljenost od centra - int
                handleCenterDistance(filtersDefaults[key]);
                break;
            case 'be_t': // Vrsta plaže - string
                handleCheckboxChange(key, value);
                break;
            case 'land': // Dostupnost lokacije - string
                handleCheckboxChange(key, value);
                break;
            case 'f_score': // Ocjena - string
                handleAccommodationScore('0');
                break;
            case 'b_b': // Best buy - int
                handleFilterChange(key, filtersDefaults[key]);
                break;
            default:
                break;
        }
    };

    return (
        <div className={`search-filters ${isForModal ? 'search-filters--modal' : ''}`}>
            <div className={`search-filters__sort ${!isForModal ? 'd-none' : ''}`}>
                <label htmlFor="sortSelectFilter" className="search-filters__sort-title">{sortData.title}</label>
                <select
                    id="sortSelectFilter"
                    className="search-filters__sort-select"
                    onChange={(e) => handleSort(e, null, false)}
                    value={selectedFilters.sort}
                >
                    {sortData.options.map((item, index) => (
                        <option value={item.value} key={index}>
                            {item.title}
                        </option>
                    ))}
                </select>
            </div>
            {getSelectedFiltersForChips?.length > 0 && (
                <div className={`search-filters__chips ${!isForModal ? 'd-none d-lg-block' : ''}`}>
                    <div className="search-filters__chips-header">
                        <h3 className="search-filters__chips-title">
                            {data.selectedFiltersText} ({getSelectedFiltersForChips.length})
                        </h3>
                        <button type="button" className="search-filters__chips-clear-filters-button" onClick={handleResetFilters}>
                            {data.removeAllText}
                        </button>
                    </div>
                    <div className="search-filters__chips-items">
                        {getSelectedFiltersForChips.map(({ key, value, text }, chipIndex) => (
                            <span key={chipIndex} className={`search-filters__chips-item ${!expandedFilters.filterChips && chipIndex > indexOfLastFilterItemToShowWhenCollapsed ? 'd-none' : ''}`}>
                                <span className="search-filters__chips-item-text">
                                    {text}
                                </span>
                                <button
                                    type="button"
                                    aria-label="Clear filters"
                                    className="search-filters__chips-item-clear-button"
                                    onClick={() => handleFilterChipDelete(key, value)}
                                >
                                    <MdClose size="16" className="search-filters__chips-item-clear-button-icon" />
                                </button>
                            </span>
                        ))}
                        {!expandedFilters.filterChips && getSelectedFiltersForChips.length > numberOfFilterItemsToShowWhenCollapsed && (
                            <button
                                type="button"
                                className="search-filters__chips-expand-button"
                                onClick={() => setExpandedFilters((prev) => ({ ...prev, filterChips: true }))}
                            >
                                +{getSelectedFiltersForChips.length - numberOfFilterItemsToShowWhenCollapsed}
                            </button>
                        )}
                    </div>
                    {getSelectedFiltersForChips.length > numberOfFilterItemsToShowWhenCollapsed && (
                        <button
                            type="button"
                            className="search-filters__show-all-button"
                            onClick={() => setExpandedFilters((prev) => ({ ...prev, filterChips: !prev.filterChips }))}
                        >
                            {expandedFilters.filterChips ? data.showLessText : data.showMoreText}
                        </button>
                    )}
                </div>
            )}
            <div className={`${isForModal ? '' : 'search-filters__container d-none d-lg-block'}`}>
                <div className="filters-modal__wrapper">
                    <p className="filters-modal__wrapper-h4">{data.filters.accommodationType.title}</p>
                    {isForModal ? (
                        <div className="filters-modal__element">
                            {data.filters.accommodationType.elements.map((element, i) => (
                                <button
                                    key={i}
                                    type="button"
                                    className={`filters__checkbox-button ${
                                        selectedFilters[element.name] && selectedFilters[element.name].split('|').includes(element.value)
                                            ? 'filters__checkbox-button--selected'
                                            : ''
                                    }`}
                                    onClick={() => handleCheckboxChange(element.name, element.value)}
                                >
                                    <img className="filters__checkbox-button-image" src={element.image.sourceURL} alt={element.image.altText} title={element.image.titleText} />
                                    <span>{element.title}</span>
                                </button>
                            ))}
                        </div>
                    ) : (
                        data.filters.accommodationType.elements.map((element, i) => (
                            <div className="filtersModal--checkbox" key={i}>
                                <CheckBoxField
                                    fieldData={{ id: `${element.name}-${i}${isForModal ? '-modal' : ''}`, name: element.name, commentText: element.title }}
                                    currentStateValue={selectedFilters[element.name] && selectedFilters[element.name].split('|').includes(element.value)}
                                    onChangeHandler={() => handleCheckboxChange(element.name, element.value)}
                                    reverse={isForModal}
                                />
                            </div>
                        ))
                    )}
                </div>
                <div className="filters-modal__wrapper priceRange">
                    <p className="filters-modal__wrapper-h4">{data.filters.priceRange.title}</p>
                    <div className={`filters-modal__inner-wrapper ${isForModal ? 'disable-modal-swipe-to-close' : ''}`}>
                        {isForModal && (
                            <>
                                <svg className="price-range-svg" xmlns="http://www.w3.org/2000/svg" width="100%" viewBox="0 0 556 54" fill="none">
                                    <path d="M0 42C0 40.8954 0.895431 40 2 40H34C35.1046 40 36 40.8954 36 42V54H0V42Z" fill="#9BDAFC" opacity="0" />
                                    <path d="M40 42C40 40.8954 40.8954 40 42 40H74C75.1046 40 76 40.8954 76 42V54H40V42Z" fill="#9BDAFC" />
                                    <path d="M80 33C80 31.8954 80.8954 31 82 31H114C115.105 31 116 31.8954 116 33V54H80V33Z" fill="#9BDAFC" />
                                    <path d="M120 22C120 20.8954 120.895 20 122 20H154C155.105 20 156 20.8954 156 22V54H120V22Z" fill="#9BDAFC" />
                                    <path d="M160 25C160 23.8954 160.895 23 162 23H194C195.105 23 196 23.8954 196 25V54H160V25Z" fill="#9BDAFC" />
                                    <path d="M200 12C200 10.8954 200.895 10 202 10H234C235.105 10 236 10.8954 236 12V54H200V12Z" fill="#9BDAFC" />
                                    <path d="M240 7C240 5.89543 240.895 5 242 5H274C275.105 5 276 5.89543 276 7V54H240V7Z" fill="#9BDAFC" />
                                    <path d="M280 2C280 0.895429 280.895 0 282 0H314C315.105 0 316 0.895431 316 2V54H280V2Z" fill="#9BDAFC" />
                                    <path d="M320 10C320 8.89543 320.895 8 322 8H354C355.105 8 356 8.89543 356 10V54H320V10Z" fill="#9BDAFC" />
                                    <path d="M360 12C360 10.8954 360.895 10 362 10H394C395.105 10 396 10.8954 396 12V54H360V12Z" fill="#9BDAFC" />
                                    <path d="M400 33C400 31.8954 400.895 31 402 31H434C435.105 31 436 31.8954 436 33V54H400V33Z" fill="#9BDAFC" />
                                    <path d="M440 42C440 40.8954 440.895 40 442 40H474C475.105 40 476 40.8954 476 42V54H440V42Z" fill="#9BDAFC" />
                                    <path d="M480 51C480 49.8954 480.895 49 482 49H514C515.105 49 516 49.8954 516 51V54H480V51Z" fill="#9BDAFC" />
                                    <path d="M520 51C520 49.8954 520.895 49 522 49H554C555.105 49 556 49.8954 556 51V54H520V51Z" fill="#9BDAFC" opacity="0" />
                                </svg>
                                <RangeSlider
                                    min={data.filters.priceRange.rangeSlider.price_range_min}
                                    max={data.filters.priceRange.rangeSlider.price_range_max}
                                    onMinChange={handleMinChange}
                                    onMaxChange={handleMaxChange}
                                    stateMin={selectedFilters.prc_min}
                                    stateMax={selectedFilters.prc_max}
                                />
                            </>
                        )}
                        <div className="filters__price-range-inputs-container">
                            <div className="filters__price-range-input-container">
                                <input
                                    className="filters__price-range-input filters__price-range-input--min"
                                    type="text"
                                    value={selectedFilters.prc_min}
                                    name="prc_min"
                                    inputMode="numeric"
                                    aria-label="Minimum price"
                                    onChange={(e) => { handleMinChange(parseInt(e.target.value, 10)); }}
                                />
                                <span className="filters__price-range-currency">€</span>
                            </div>
                            {!isForModal && <span>{data.filters.priceRange.upToText}</span>}
                            <div className="filters__price-range-input-container">
                                <input
                                    className="filters__price-range-input filters__price-range-input--max"
                                    type="text"
                                    value={selectedFilters.prc_max}
                                    name="prc_max"
                                    inputMode="numeric"
                                    aria-label="Maximum price"
                                    onChange={(e) => { handleMaxChange(parseInt(e.target.value, 10)); }}
                                />
                                <span className="filters__price-range-currency">€</span>
                            </div>
                        </div>
                        <div className="search-filters__only-on-sale-container">
                            <CheckBoxField
                                fieldData={{ name: data.filters.priceRange.onlyOnSale.name, commentText: data.filters.priceRange.onlyOnSale.title }}
                                currentStateValue={selectedFilters.dc_t}
                                onChangeHandler={() => handleFilterChange(data.filters.priceRange.onlyOnSale.name, 'all_discounts')}
                                beforeIcon={isForModal && <MdOutlinePercent size="24" className="search-filters__checkbox-icon" />}
                                reverse={isForModal}
                            />
                        </div>
                    </div>
                </div>
                <div className="filters-modal__wrapper">
                    <p className="filters-modal__wrapper-h4">{data.filters.parts.title}</p>
                    <div>
                        <div className="search-filters__rooms-select-count-container">
                            {data.filters.parts.numberBoxElements.map((element, i) => (
                                <div className="filters-modal__rooms" key={i}>
                                    <div className="filters-modal__left-wrapper">
                                        {isForModal && (
                                            <img className="filters-modal__left-wrapper-img" src={element.image.sourceURL} alt={element.image.altText} title={element.image.titleText} />
                                        )}
                                        <span>{element.title}</span>
                                    </div>
                                    <div className="filters-modal__right-wrapper">
                                        <button type="button" className="form__button--minus" onClick={() => handleBathrooms(element.name, 'minus')} aria-label="Decrease number of bathrooms">
                                            <AiOutlineMinus />
                                        </button>
                                        <input
                                            name={element.name}
                                            id={element.name}
                                            type="text"
                                            value={selectedFilters[element.name]}
                                            readOnly={true}
                                            aria-label={element.title}
                                            className="search-filters__rooms-select-count-input"
                                        />
                                        <button type="button" className="form__button--plus" onClick={() => handleBathrooms(element.name, 'plus')} aria-label="Decrease number of bathrooms">
                                            <AiOutlinePlus />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div>
                            {data.filters.parts.checkBoxElements.map((elementTwo, i) => (
                                <div className={`filtersModal--checkbox ${i % 2 === 0 ? 'search-filters__checkbox--white-bg' : ''}`} key={i}>
                                    <CheckBoxField
                                        fieldData={{ id: `${elementTwo.name}-${i}${isForModal ? '-modal' : ''}`, name: elementTwo.name, commentText: elementTwo.title }}
                                        currentStateValue={selectedFilters[elementTwo.name]}
                                        onChangeHandler={() => handleFilterChange(elementTwo.name, elementTwo.value)}
                                        beforeIcon={isForModal && <img src={elementTwo.image.sourceURL} alt={elementTwo.image.altText} title={elementTwo.image.titleText} />}
                                        reverse={isForModal}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
                <div className="filters-modal__wrapper additionalServices">
                    <p className="filters-modal__wrapper-h4">{data.filters.additionalServices.title}</p>
                    {data.filters.additionalServices.elements.map((element, i) => (
                        <div
                            className={`filtersModal--checkbox
                                ${i % 2 === 0 ? 'search-filters__checkbox--white-bg' : ''}
                                ${!isForModal && !expandedFilters.additionalServices && i > indexOfLastFilterItemToShowWhenCollapsed ? 'd-none' : ''}
                            `}
                            key={i}
                        >
                            <CheckBoxField
                                fieldData={{ id: `${element.name}-${i}${isForModal ? '-modal' : ''}`, name: element.name, commentText: element.title }}
                                currentStateValue={selectedFilters[element.name]}
                                onChangeHandler={() => handleFilterChange(element.name, '')}
                                beforeIcon={isForModal && <img src={element.image.sourceURL} alt={element.image.altText} title={element.image.titleText} />}
                                reverse={isForModal}
                            />
                        </div>
                    ))}
                    {!isForModal && (
                        <button
                            type="button"
                            className="search-filters__show-all-button"
                            onClick={() => setExpandedFilters((prev) => ({ ...prev, additionalServices: !prev.additionalServices }))}
                        >
                            {expandedFilters.additionalServices ? data.showLessText : data.showMoreText}
                        </button>
                    )}
                </div>
                <div className="filters-modal__wrapper accommodationCategory">
                    <p className="filters-modal__wrapper-h4">{data.filters.accommodationCategory.title}</p>
                    {isForModal ? (
                        <div className="filters-modal__element">
                            {data.filters.accommodationCategory.elements.map((element, i) => (
                                <button
                                    key={i}
                                    type="button"
                                    className={`filters__checkbox-button
                                        ${selectedFilters[element.name] && selectedFilters[element.name].split('|').includes(element.value) ? 'filters__checkbox-button--selected' : ''}
                                    }`}
                                    onClick={() => handleCheckboxChange(element.name, element.value)}
                                >
                                    <img className="filters__checkbox-button-image" src={element.image.sourceURL} alt={element.image.altText} title={element.image.titleText} />
                                    <span>{element.title}</span>
                                </button>
                            ))}
                        </div>
                    ) : (
                        <>
                            {data.filters.accommodationCategory.elements.map((element, i) => (
                                <div
                                    key={i}
                                    className={`filtersModal--checkbox
                                        ${!expandedFilters.accommodationCategory && i > indexOfLastFilterItemToShowWhenCollapsed ? 'd-none' : ''}
                                    `}
                                >
                                    <CheckBoxField
                                        fieldData={{ id: `${element.name}-${i}${isForModal ? '-modal' : ''}`, name: element.name, commentText: element.title }}
                                        currentStateValue={selectedFilters[element.name] && selectedFilters[element.name].split('|').includes(element.value)}
                                        onChangeHandler={() => handleCheckboxChange(element.name, element.value)}
                                    />
                                </div>
                            ))}
                            <button
                                type="button"
                                className="search-filters__show-all-button"
                                onClick={() => setExpandedFilters((prev) => ({ ...prev, accommodationCategory: !prev.accommodationCategory }))}
                            >
                                {expandedFilters.accommodationCategory ? data.showLessText : data.showMoreText}
                            </button>
                        </>
                    )}
                </div>
                <div className="filters-modal__wrapper">
                    <p className="filters-modal__wrapper-h4">{data.filters.unitPosition.title}</p>
                    {isForModal ? (
                        <div className="filters-modal__element filters__unit-position">
                            {data.filters.unitPosition.elements.map((element, i) => (
                                <button
                                    key={i}
                                    type="button"
                                    className={`filters__checkbox-button ${
                                        selectedFilters[element.name] && selectedFilters[element.name].split('|').includes(element.value.toString())
                                            ? 'filters__checkbox-button--selected'
                                            : ''
                                    }`}
                                    onClick={() => handleCheckboxChange(element.name, element.value.toString())}
                                >
                                    <img className="filters__checkbox-button-image" src={element.image.sourceURL} alt={element.image.altText} title={element.image.titleText} />
                                    <span>{element.title}</span>
                                </button>
                            ))}
                        </div>
                    ) : (
                        data.filters.unitPosition.elements.map((element, i) => (
                            <div className="filtersModal--checkbox" key={i}>
                                <CheckBoxField
                                    fieldData={{ id: `${element.name}-${i}${isForModal ? '-modal' : ''}`, name: element.name, commentText: element.title }}
                                    currentStateValue={selectedFilters[element.name] && selectedFilters[element.name].split('|').includes(element.value)}
                                    onChangeHandler={() => handleCheckboxChange(element.name, element.value.toString())}
                                />
                            </div>
                        ))
                    )}
                </div>
                <div className="filters-modal__wrapper">
                    <p className="filters-modal__wrapper-h4">{data.filters.locationAvailability.title}</p>
                    {data.filters.locationAvailability.elements.map((element, i) => (
                        <div className={`filtersModal--checkbox ${i % 2 === 0 ? 'search-filters__checkbox--white-bg' : ''}`} key={i}>
                            <CheckBoxField
                                fieldData={{ id: `${element.name}-${i}${isForModal ? '-modal' : ''}`, name: element.name, commentText: element.title }}
                                currentStateValue={selectedFilters[element.name] && selectedFilters[element.name].split('|').includes(element.value)}
                                onChangeHandler={() => handleCheckboxChange(element.name, element.value)}
                                reverse={isForModal}
                            />
                        </div>
                    ))}
                </div>
                <div className="filters-modal__wrapper beach-distance">
                    <p className="filters-modal__wrapper-h4">{data.filters.distances.title}</p>
                    <div className="filters__beach-distance-container">
                        <p className="filters__beach-distance-container-h5">
                            {isForModal && (
                                <img className="search-filters__distance-section-title-image" src={data.filters.distances.beach.image.sourceURL} alt={data.filters.distances.beach.image.altText} title={data.filters.distances.beach.image.titleText} />
                            )}
                            {data.filters.distances.beach.title}
                        </p>
                        {data.filters.distances.beach.elements.map((element, i) => (
                            <div className={`beach-distance__element ${i % 2 === 0 ? 'search-filters__radio--white-bg' : ''}`} key={i}>
                                <RadioBoxField
                                    currentItem={{ value: parseInt(element.value, 10), id: `${element.id}${isForModal ? '-modal' : ''}`, title: `${i > 0 ? '< ' : ''}${element.title}` }}
                                    fieldData={{ name: element.name }}
                                    currentStateValue={selectedFilters.be_d}
                                    defaultStateValue={defaultCheckedBeach}
                                    onChangeHandler={() => handleBeachDistance(parseInt(element.value, 10))}
                                    reverse={isForModal}
                                />
                            </div>
                        ))}
                    </div>
                    <div className="filters__center-distance-container">
                        <p className="filters__beach-distance-container-h5">
                            {isForModal && (
                                <img className="search-filters__distance-section-title-image" src={data.filters.distances.placeCenter.image.sourceURL} alt={data.filters.distances.placeCenter.image.altText} title={data.filters.distances.placeCenter.image.titleText} />
                            )}
                            {data.filters.distances.placeCenter.title}
                        </p>
                        {data.filters.distances.placeCenter.elements.map((element, i) => (
                            <div className={`beach-distance__element ${i % 2 === 0 ? 'search-filters__radio--white-bg' : ''}`} key={i}>
                                <RadioBoxField
                                    currentItem={{ value: parseInt(element.value, 10), id: `${element.id}${isForModal ? '-modal' : ''}`, title: `${i > 0 ? '< ' : ''}${element.title}` }}
                                    fieldData={{ name: element.name }}
                                    currentStateValue={selectedFilters.ce_d}
                                    defaultStateValue={defaultCheckedCenter}
                                    onChangeHandler={() => handleCenterDistance(parseInt(element.value, 10))}
                                    reverse={isForModal}
                                />
                            </div>
                        ))}
                    </div>
                </div>
                <div className="filters-modal__wrapper">
                    <p className="filters-modal__wrapper-h4">{data.filters.beachType.title}</p>
                    {data.filters.beachType.elements.map((element, i) => (
                        <div className={`filtersModal--checkbox ${i % 2 === 0 ? 'search-filters__checkbox--white-bg' : ''}`} key={i}>
                            <CheckBoxField
                                fieldData={{ id: `${element.name}-${i}${isForModal ? '-modal' : ''}`, name: element.name, commentText: element.title }}
                                currentStateValue={selectedFilters[element.name] && selectedFilters[element.name].split('|').includes(element.value)}
                                onChangeHandler={() => handleCheckboxChange(element.name, element.value)}
                                reverse={isForModal}
                            />
                        </div>
                    ))}
                </div>
                <div className="filters-modal__wrapper accommodation-score">
                    <p className="filters-modal__wrapper-h4">{data.filters.accommodationScoreNew.title}</p>
                    <div className="filters__accommodation-score-container">
                        {data.filters.accommodationScoreNew.elements.map((element, i) => (
                            <div className={`filtersModal--checkbox ${i % 2 === 0 ? 'search-filters__radio--white-bg' : ''}`} key={i}>
                                <RadioBoxField
                                    currentItem={{ value: element.value, id: `${element.id}${isForModal ? '-modal' : ''}`, title: element.title }}
                                    fieldData={{ name: element.name }}
                                    currentStateValue={selectedFilters.f_score}
                                    defaultStateValue={`${data.filters.accommodationScoreNew.defaultSelectedElementValue}`}
                                    onChangeHandler={() => handleAccommodationScore(element.value)}
                                    afterIcon={isForModal && i !== 0 && <AiFillStar size="16" className="search-filters__rating-after-icon" />}
                                    beforeIcon={!isForModal && i !== 0 && <AiFillStar size="16" className="search-filters__rating-before-icon" />}
                                    reverse={isForModal}
                                />
                            </div>
                        ))}
                    </div>
                </div>
                {!isForModal && initiallySelectedFilters && !isEqual(selectedFilters, initiallySelectedFilters) && (
                    <button
                        type="button"
                        className="search-results__filters-btn d-none d-lg-block"
                        onClick={handleApplyFilters}
                        disabled={isLoading || searchResultsCount === 0}
                    >
                        {searchResultsCount === 0 ? (
                            <span>{data.noResultsText}</span>
                        ) : (
                            <span>
                                <span>{data.filterText}</span> <span>({searchResultsCount})</span>
                            </span>
                        )}
                    </button>
                )}
            </div>
        </div>
    );
}
